import { useState, useEffect } from "react";
import { HabitColor, predefinedColors, defaultColors } from "@/context/SupabaseHabitContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { 
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ColorPickerProps {
  selectedColor: HabitColor;
  onSelectColor: (color: HabitColor) => void;
  onUpgradeRequired?: () => void;
}

export default function ColorPicker({
  selectedColor,
  onSelectColor,
  onUpgradeRequired,
}: ColorPickerProps) {
  const { isPremium } = useAuth();
  const [localSelectedColor, setLocalSelectedColor] = useState<HabitColor>(selectedColor);

  // Update local state when selectedColor prop changes
  useEffect(() => {
    setLocalSelectedColor(selectedColor);
  }, [selectedColor]);

  const handleColorSelect = (color: HabitColor) => {
    // Check if user is trying to select a non-default color
    const isDefaultColor = color.filled === defaultColors.filled;
    
    if (!isPremium && !isDefaultColor) {
      // Call the upgrade callback if provided, otherwise do nothing
      if (onUpgradeRequired) {
        onUpgradeRequired();
      }
      return; // Don't update color or call parent callback
    }
    
    // Allow the color change for premium users or default color selection
    setLocalSelectedColor(color);
    onSelectColor(color);
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {predefinedColors.map((color, index) => {
        const isDefaultColor = color.filled === defaultColors.filled;
        const isSelected = localSelectedColor.filled === color.filled;
        
        return (
        <Tooltip key={index}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-6 h-6 rounded-full transition-all",
                "border-2",
                  isSelected 
                  ? "border-foreground scale-110" 
                    : "border-transparent hover:scale-105",
                  // Add visual indicator for premium colors
                  !isPremium && !isDefaultColor && "opacity-60 hover:opacity-80"
              )}
              style={{ backgroundColor: color.filled }}
              onClick={() => handleColorSelect(color)}
                aria-label={`Color option ${index + 1}${!isPremium && !isDefaultColor ? ' (Premium)' : ''}`}
            />
          </TooltipTrigger>
          <TooltipContent side="bottom">
              {isDefaultColor ? 'Default Color' : !isPremium ? 'Premium Color' : `Color ${index + 1}`}
          </TooltipContent>
        </Tooltip>
        );
      })}
    </div>
  );
}
